"use client";

import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { PanchangCityProvider, usePanchangCity } from "@/contexts/PanchangCity";
import { trackOwnedAnalyticsOnly } from "@/lib/analytics-events";
import { getPanchang, type PanchangResponse } from "@/lib/api/panchang";
import { getBrowserQueryClient } from "@/lib/query-client";
import {
  ControlMainTithiCard,
  ControlPanchangSnapshot,
  ControlSignificanceCard,
  ControlTithiHeader,
  ControlTithiTypesReference,
} from "./tithi-today.control-sections";

export interface TithiTodayControlProps {
  showHeader?: boolean;
  language?: "en" | "hi";
  sourcePath?: "/tithi-today" | "/hi/tithi-today";
  initialDate: string;
  initialResult?: PanchangResponse | null;
  crossLinks: ReactNode;
}

export default function TithiTodayControl(props: TithiTodayControlProps) {
  return (
    <PanchangCityProvider>
      <TithiTodayControlContent {...props} />
    </PanchangCityProvider>
  );
}

function TithiTodayControlContent({
  showHeader = true,
  language = "en",
  sourcePath = "/tithi-today",
  initialDate,
  initialResult = null,
  crossLinks,
}: TithiTodayControlProps) {
  const { city } = usePanchangCity();
  const date = useMemo(() => initialDate, [initialDate]);
  const initialData =
    city.slug === "new-delhi" && initialResult?.date === date ? initialResult : undefined;
  const {
    data: result,
    isLoading,
    isError,
  } = useQuery(
    {
      queryKey: ["panchang", city.slug, date],
      queryFn: () =>
        getPanchang({
          date,
          latitude: city.latitude,
          longitude: city.longitude,
          tz: city.timezone,
        }),
      initialData,
      staleTime: 30 * 60 * 1000,
      retry: 1,
    },
    getBrowserQueryClient()
  );
  const tithi = result?.tithi;
  const cityName = language === "hi" ? (city.name_hi ?? city.name) : city.name;
  useTithiAnswerRendered(result, city.slug, language, sourcePath);

  return (
    <div className="mx-auto max-w-2xl px-4 pb-10 pt-4 sm:px-6" data-daily-timing-surface="control">
      {showHeader && (
        <ControlTithiHeader language={language} date={date} tithi={tithi} cityName={cityName} />
      )}
      <ControlMainTithiCard
        language={language}
        isLoading={isLoading}
        isError={isError}
        tithi={tithi}
        timezone={city.timezone}
        date={result?.date}
      />
      <ControlSignificanceCard language={language} isLoading={isLoading} tithi={tithi} />
      <ControlPanchangSnapshot
        language={language}
        isLoading={isLoading}
        result={result}
        cityName={cityName}
      />
      <ControlTithiTypesReference language={language} />
      {crossLinks}
    </div>
  );
}

/**
 * The governed reach-SLI denominator for tithi_today_result
 * (value_surface_placements.yaml): one owned event per rendered engine answer
 * per date and city. It used to fire only inside the retired
 * daily_timing_v2_pilot_2026_07 arms.
 */
function useTithiAnswerRendered(
  result: PanchangResponse | undefined,
  citySlug: string,
  language: "en" | "hi",
  sourcePath: string
) {
  const emitted = useRef(new Set<string>());
  useEffect(() => {
    if (!result) return;
    const key = `${result.date}:${citySlug}`;
    if (emitted.current.has(key)) return;
    emitted.current.add(key);
    trackOwnedAnalyticsOnly("daily_timing_answer_rendered", {
      language,
      source_path: sourcePath,
      engine_result_date: result.date,
      city_slug: citySlug,
    });
  }, [citySlug, language, result, sourcePath]);
}
